import { SelectOption } from '@pluxee-design-system/core';
import { useContractProductLocations } from 'api/contractApi';
import SelectControl from 'common/forms/SelectControl';
import useCurrentContract from 'data/useCurrentContract';
import useTranslations from 'i18n';
import { debounce, isArray } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import { InputActionMeta, MultiValue, PropsValue, SingleValue } from 'react-select';
import OptionComponent from './OptionComponent';
import {
  LocationSelectOnChangeProps,
  LocationSelectProps,
  LocationType,
  OptionComponentProps,
  ProductLocationFilterValues,
} from './types';
import { getLabel, getValue, id } from './utils';

const LocationSelect = ({
  getDefaultOption,
  getOptionLabel = getLabel,
  getOptionValue = getValue,
  defaultFilterState,
  disabled: _skipDisabled,
  options,
  onChange,
  onTrackChange,
  hasNextPage: optionsHasNextPage,
  // isEmpty: optionsIsEmpty,
  isCompact,
  isMulti,
  name,
  isNextPageLoading: optionsIsNextPageLoading,
  loadNextPage: optionsLoadNextPage,
  filterState: optionsFilterState,
  setFilterState: optionsSetFilterState,
  ...rest
}: LocationSelectProps & LocationSelectOnChangeProps) => {
  const { t } = useTranslations();
  const { field } = useController({ name });
  const { onChange: fieldOnChange } = field;
  const pageRef = useRef(0);
  const hasMountedRef = useRef(false);
  const [inputValue, setInputValue] = useState('');
  const value = field.value || null;
  const hasOptions = options !== undefined;
  const scrollOffsetRef = useRef<number>(0);
  const [dataFilterState, setDataFilterState] = useState<Partial<ProductLocationFilterValues>>(
    () => ({
      ...defaultFilterState,
    }),
  );
  const { contractId } = useCurrentContract(!hasOptions); // to disable
  const {
    data: locations,
    hasNextPage: dataHasNextPage,
    // isEmpty: dataIsEmpty,
    isLoadingPage: dataIsLoadingPage,
    loadNextPage: dataLoadNextPage,
  } = useContractProductLocations(
    !hasOptions ? contractId : undefined, // to disable
    undefined,
    true,
    dataFilterState,
  );

  // use options or fetch data?
  const data = (hasOptions ? options : locations) as LocationType[];
  const hasNextPage = optionsHasNextPage ?? dataHasNextPage;
  // const isEmpty = optionsIsEmpty ?? dataIsEmpty;
  const isLoadingPage = optionsIsNextPageLoading ?? dataIsLoadingPage;
  const loadNextPage = optionsLoadNextPage ?? dataLoadNextPage;
  const filterState = optionsFilterState ?? dataFilterState;
  const setFilterState = optionsSetFilterState ?? setDataFilterState;

  const debouncedSetInputValue = useMemo(
    () =>
      debounce((fulltext: string) => {
        scrollOffsetRef.current = 0; // reset scroll
        setFilterState((p) => ({ ...p, fulltext }));
      }, 1000),
    [setFilterState],
  );
  const handleScrolledToBottom = useCallback(async () => {
    if (!isLoadingPage && hasNextPage) {
      const pageSize = 20;
      await loadNextPage(pageSize * pageRef.current, pageSize * (pageRef.current + 1));
      pageRef.current += 1;
    }
  }, [isLoadingPage, hasNextPage, loadNextPage]);
  const handleInputChange = useCallback(
    (newValue: string, { action }: InputActionMeta) => {
      if (isMulti && action === 'set-value') {
        return false;
      }
      if (action === 'input-change') {
        setInputValue(newValue);
        debouncedSetInputValue(newValue);
      } else if (action === 'set-value') {
        // Reset input when the menu closes without selection
        setInputValue('');
      }
    },
    [isMulti, debouncedSetInputValue, setInputValue],
  );
  const handleChange = useCallback(
    (option: PropsValue<SelectOption>) => {
      // @ts-expect-error Just pass it as is
      onTrackChange?.(option);
      if (onChange) {
        // @ts-expect-error Just pass it as is
        onChange(option);
      } else {
        fieldOnChange(
          isMulti
            ? (option as MultiValue<SelectOption>).map((item: SelectOption) =>
                getOptionValue(item as unknown as LocationType),
              )
            : getOptionValue(option as unknown as LocationType),
        );
      }
    },
    [getOptionValue, isMulti, onChange, onTrackChange, fieldOnChange],
  );
  const currentValue = useMemo<PropsValue<SelectOption>>(() => {
    if (data) {
      if (isMulti) {
        const normalizedValue = isArray(value) ? value : [value];
        const usedValues = new Set<string>();
        const result = data.filter((option) => {
          const optionValue = getOptionValue(option);
          if (normalizedValue.includes(optionValue)) {
            usedValues.add(optionValue);
            return true;
          }
          return false;
        });
        // values not present in options
        normalizedValue.forEach((val) => {
          const optionValue = getDefaultOption?.(val);
          if (val && !usedValues.has(val) && optionValue) {
            result.push(optionValue);
          }
        });
        return result as unknown as MultiValue<SelectOption>;
      }
      return (data.find((option) => getOptionValue(option as LocationType) == value) ||
        getDefaultOption?.(value as unknown as string) ||
        null) as unknown as SingleValue<SelectOption>;
    }
    return isMulti ? ([] as MultiValue<SelectOption>) : (null as SingleValue<SelectOption>);
  }, [data, isMulti, getDefaultOption, getOptionValue, value]);

  const Option = useMemo(
    () => (props: OptionComponentProps) => OptionComponent(props, { isCompact, t }),
    [isCompact, t],
  );

  useEffect(
    () => setDataFilterState({ ...defaultFilterState }),
    [defaultFilterState, setDataFilterState],
  );

  // Each time the filterState prop changed we reset page counter to clear the cache
  useEffect(() => {
    if (hasMountedRef.current) {
      pageRef.current = 0;
    }
    hasMountedRef.current = true;
  }, [filterState]);

  return (
    <SelectControl
      // react-selects props passed by ...rest
      {...rest}
      value={currentValue}
      name={name}
      isMulti={isMulti}
      onBlur={field.onBlur}
      onChange={handleChange}
      customComponents={{ Option }}
      sortAlphabetically={false}
      // @ts-expect-error Passed by ...rest
      options={data}
      isSearchInput
      // @ts-expect-error Fixed by getOptionValue
      filterOption={id}
      isClearable={rest.isClearable && !isLoadingPage}
      isLoading={isLoadingPage}
      isSearchable={true}
      // @ts-expect-error Fixed by getOptionValue
      getOptionLabel={getOptionLabel}
      // @ts-expect-error Fixed by getOptionValue
      getOptionValue={getOptionValue}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onMenuScrollToBottom={handleScrolledToBottom}
      // menuIsOpen
      // menuShouldBlockScroll
    />
  );
};

export default LocationSelect;
